import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Chip,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  InputAdornment,
  ToggleButton,
  ToggleButtonGroup,
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSnackbar } from 'notistack';
import { PageShell, DataTable, type DataTableColumn, RouteActionButton } from '../../components';
import { pode } from '../../authorization/pode';
import psychosocialService from '../../services/psychosocialService';
import { PsychosocialCampaign, PsychosocialCampaignStatus } from '../../types/psychosocial';
import { useAcao } from '../../authorization/useVeredito';

interface Props {
  embedded?: boolean;
}

const statusLabels: Record<PsychosocialCampaignStatus, string> = {
  draft: 'Rascunho',
  active: 'Ativa',
  closed: 'Fechada',
  archived: 'Arquivada',
};

const statusColors: Record<PsychosocialCampaignStatus, 'default' | 'primary' | 'success' | 'warning' | 'error' | 'info'> = {
  draft: 'default',
  active: 'success',
  closed: 'info',
  archived: 'warning',
};

const getResponseError = (err: unknown, fallback: string): string => {
  const responseError = (err as { response?: { data?: { error?: unknown } } })?.response?.data?.error;
  return typeof responseError === 'string' ? responseError : fallback;
};

const columns: DataTableColumn<PsychosocialCampaign>[] = [
  {
    id: 'name',
    label: 'Nome',
    primary: true,
    render: (campaign) => <Typography fontWeight="medium">{campaign.name}</Typography>,
  },
  {
    id: 'company',
    label: 'Empresa',
    render: (campaign) => campaign.company?.tradeName || campaign.company?.legalName || '-',
  },
  {
    id: 'form',
    label: 'Formulário',
    render: (campaign) => campaign.form?.name || '-',
  },
  {
    id: 'status',
    label: 'Status',
    render: (campaign) => (
      <Chip
        size="small"
        label={statusLabels[campaign.status]}
        color={statusColors[campaign.status]}
      />
    ),
  },
  {
    id: 'endDate',
    label: 'Data Limite',
    render: (campaign) =>
      campaign.endDate ? new Date(campaign.endDate).toLocaleDateString('pt-BR') : '-',
  },
];

const PsychosocialCampaignsPage: React.FC<Props> = ({ embedded = false }) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const excluir = useAcao('psychosocial.delete', 'Excluir');

  const [campaigns, setCampaigns] = useState<PsychosocialCampaign[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [campaignToDelete, setCampaignToDelete] = useState<PsychosocialCampaign | null>(null);
  const [deleting, setDeleting] = useState(false);

  // Mantém a busca fora das dependências: ela só dispara no Enter.
  const searchRef = useRef(search);
  searchRef.current = search;

  const loadCampaigns = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await psychosocialService.getCampaigns({
        page: page + 1,
        limit: rowsPerPage,
        search: searchRef.current || undefined,
        status: statusFilter || undefined,
      });
      setCampaigns(result.data);
      setTotalCount(result.pagination?.total || 0);
    } catch (err: unknown) {
      setError(getResponseError(err, 'Erro ao carregar campanhas'));
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, statusFilter]);

  useEffect(() => {
    loadCampaigns();
  }, [loadCampaigns]);

  const handleDelete = async () => {
    if (!campaignToDelete) return;
    setDeleting(true);
    try {
      await psychosocialService.deleteCampaign(campaignToDelete.id);
      enqueueSnackbar('Campanha removida com sucesso', { variant: 'success' });
      setDeleteDialogOpen(false);
      setCampaignToDelete(null);
      loadCampaigns();
    } catch (err: unknown) {
      enqueueSnackbar(getResponseError(err, 'Erro ao remover campanha'), { variant: 'error' });
    } finally {
      setDeleting(false);
    }
  };

  const content = (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2, flexWrap: 'wrap', gap: 1 }}>
        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          <TextField
            size="small"
            placeholder="Buscar campanhas..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && loadCampaigns()}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ width: 280 }}
          />
          <ToggleButtonGroup
            size="small"
            value={statusFilter}
            exclusive
            onChange={(_e, v) => { setStatusFilter(v || ''); setPage(0); }}
          >
            <ToggleButton value="">Todas</ToggleButton>
            <ToggleButton value="draft">Rascunho</ToggleButton>
            <ToggleButton value="active">Ativas</ToggleButton>
            <ToggleButton value="closed">Fechadas</ToggleButton>
          </ToggleButtonGroup>
        </Box>
        <RouteActionButton
          para="/psychosocial/campaigns/new"
          exige={pode('psychosocial.create')}
          variant="contained"
          startIcon={<AddIcon />}
        >
          Nova Campanha
        </RouteActionButton>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <DataTable
        columns={columns}
        rows={campaigns}
        getRowId={(campaign) => campaign.id}
        loading={loading}
        emptyState={{
          title: 'Nenhuma campanha encontrada',
          description:
            'Campanhas psicossociais partem de um formulário e reúnem os funcionários convidados a responder.',
        }}
        onRowClick={(campaign) => navigate(`/psychosocial/campaigns/${campaign.id}`)}
        actions={[
          {
            icon: <ViewIcon fontSize="small" />,
            tooltip: 'Visualizar',
            onClick: (campaign) => navigate(`/psychosocial/campaigns/${campaign.id}`),
          },
          {
            icon: <DeleteIcon fontSize="small" />,
            color: 'error',
            onClick: (campaign) => {
              setCampaignToDelete(campaign);
              setDeleteDialogOpen(true);
            },
            hidden: (campaign) => campaign.status !== 'draft',
            ...excluir,
          },
        ]}
        pagination={{
          page,
          rowsPerPage,
          totalCount,
          onPageChange: setPage,
          onRowsPerPageChange: (n) => { setRowsPerPage(n); setPage(0); },
          rowsPerPageOptions: [5, 10, 25],
        }}
      />

      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>Confirmar exclusão</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Tem certeza que deseja excluir a campanha "{campaignToDelete?.name}"?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialogOpen(false)}>Cancelar</Button>
          <Button onClick={handleDelete} color="error" disabled={deleting}>
            {deleting ? 'Excluindo...' : 'Excluir'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );

  if (embedded) return content;

  return (
    <PageShell
      breadcrumbs={[
        { label: 'Psicossocial', path: '/psychosocial' },
        { label: 'Campanhas' },
      ]}
      title="Campanhas Psicossociais"
      subtitle="Gerencie as campanhas de avaliação psicossocial"
    >
      {content}
    </PageShell>
  );
};

export default PsychosocialCampaignsPage;
